import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error" | "expired">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token found.");
      return;
    }
    fetch("/api/v1/auth/verify-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.message === "Email verified successfully" || data.message === "Email already verified") {
          setStatus("success");
        } else if (data.error === "TokenExpired") {
          setStatus("expired");
          setMessage(data.message);
        } else {
          setStatus("error");
          setMessage(data.message ?? "Verification failed.");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Network error. Please try again.");
      });
  }, []);

  const gradientBg =
    status === "success" ? "linear-gradient(135deg,#22c55e,#16a34a)"
    : status === "loading" ? "linear-gradient(135deg,#6366f1,#9333ea)"
    : "linear-gradient(135deg,#ef4444,#dc2626)";

  return (
    <div className="min-h-screen bg-[#12122A] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1B1B3D] rounded-2xl border border-white/10 p-8 text-center shadow-2xl">
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: gradientBg }}>
            {status === "loading" && <Loader2 className="w-8 h-8 text-white animate-spin" />}
            {status === "success" && <CheckCircle2 className="w-8 h-8 text-white" />}
            {(status === "error" || status === "expired") && <XCircle className="w-8 h-8 text-white" />}
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {status === "loading" && "Verifying your email…"}
          {status === "success" && "Email verified!"}
          {status === "error" && "Verification failed"}
          {status === "expired" && "Link expired"}
        </h1>
        <p className="text-white/60 text-sm mb-6">
          {status === "loading" && "Please wait while we verify your email address."}
          {status === "success" && "Your email has been verified. You can now access all CommonQR features."}
          {status === "error" && (message || "Invalid or already used verification link.")}
          {status === "expired" && "This verification link has expired. Please request a new one from the dashboard."}
        </p>
        {status === "success" && (
          <Button onClick={() => navigate("/dashboard")} className="w-full rounded-xl font-bold">
            Go to Dashboard
          </Button>
        )}
        {(status === "error" || status === "expired") && (
          <div className="space-y-3">
            <Button onClick={() => navigate("/dashboard")} className="w-full rounded-xl font-bold">
              Go to Dashboard
            </Button>
            <button onClick={() => navigate("/login")} className="w-full text-sm text-white/50 hover:text-white/80 transition-colors">
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
